import { POSITION_ATTRIBUTES } from '../api/constants'


export class PositionHistoryService {
  constructor(engine) {
    this.engine = engine;
    
    // Program and Stage IDs
    this.POSITION_HISTORY_PROGRAM = 'bzvlzdKhtFb';
    this.POSITION_HISTORY_STAGE = 'iPoMxyQ4fP2';
    
    // Data Element IDs from your database
    this.DATA_ELEMENTS = {
        ACTION_TYPE: 'AXodktztnJi',           // POS_ACTION_TYPE
        POSITION_ID: 'SlgO60cMK3F',           // Position_ID
        STAFF_MEMBER: 'X1XVG1KKc0K',          // Position Current Staff (NEW - this is correct!)
        START_DATE: 'MZHGfRa0Oqa',            // POS_START_DATE
        END_DATE: 'YsFQl51qmvd',              // POS_END_DATE
        PREVIOUS_STAFF: 'JKJwrk5tZS3',        // POS_PREV_STAFF
        NOTES: 'xoNoPDw9XPM',                 // POS_NOTES
        EFFECTIVE_DATE: 'DoAGWqub8Cz'         // Position Effective Date
      };
  }

  getDataValue(event, dataElementId) {
    if (!event.dataValues) return null;
    const dataValue = event.dataValues.find(dv => dv.dataElement === dataElementId);
    return dataValue ? dataValue.value : null;
  }

async trackStaffAssignment(positionTei, newStaffId, previousStaffId = null, notes = '', user = null, orgUnit = null) {
  try {
    // 🔴 CRITICAL: Skip if no actual staff change
    if (newStaffId === previousStaffId) {
      console.log('⚠️ No staff change detected, skipping history tracking');
      return { success: true, skipped: true };
    }
    
    // 🔴 Also skip if both are empty/VACANT
    if (!newStaffId && !previousStaffId) {
      console.log('⚠️ Both staff IDs empty, skipping history tracking');
      return { success: true, skipped: true };
    }

    if (!positionTei) {
      console.error('❌ Invalid TEI ID for history tracking:', positionTei);
      return { success: false, error: 'Invalid TEI ID' };
    }

    const eventDate = new Date().toISOString();
    const effectiveDate = eventDate.split('T')[0];
    
    console.log('🔍 [DEBUG] Starting history tracking for TEI:', positionTei);
    
    let actualPositionId = positionTei;

    // ONLY fetch TEI details if orgUnit is not provided
    if (!orgUnit) {
      try {
        const positionResponse = await this.engine.query({
          tei: {
            resource: 'trackedEntityInstances',
            id: positionTei,
            params: {
              fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
            }
          }
        });

        if (positionResponse.tei) {
          orgUnit = positionResponse.tei.orgUnit;
          
          const attributes = positionResponse.tei.attributes || [];
          const positionIdAttr = attributes.find(attr => 
            attr.attribute === 'SlgO60cMK3F'
          );
          if (positionIdAttr) {
            actualPositionId = positionIdAttr.value;
          }
          
          console.log('✅ [DEBUG] Position TEI belongs to orgUnit:', orgUnit);
          console.log('✅ [DEBUG] Position ID from attributes:', actualPositionId);
        }
      } catch (teiError) {
        console.error('❌ CRITICAL: Could not fetch TEI details:', teiError.message);
        return { success: false, error: 'Could not fetch TEI details' };
      }
    }

    if (!orgUnit) {
      console.error('❌ CRITICAL: No orgUnit available for position');
      return { success: false, error: 'No orgUnit available' };
    }

    console.log('🎯 [DEBUG] Creating history event in orgUnit:', orgUnit);

    const eventData = {
      program: this.POSITION_HISTORY_PROGRAM,
      programStage: this.POSITION_HISTORY_STAGE,
      orgUnit: orgUnit,
      eventDate: effectiveDate,
      status: 'COMPLETED',
      dataValues: [
        { dataElement: this.DATA_ELEMENTS.ACTION_TYPE, value: 'STAFF_ASSIGNMENT' },
        { dataElement: this.DATA_ELEMENTS.POSITION_ID, value: actualPositionId },
        { dataElement: this.DATA_ELEMENTS.STAFF_MEMBER, value: newStaffId || '' },
        { dataElement: this.DATA_ELEMENTS.START_DATE, value: effectiveDate },
        { dataElement: this.DATA_ELEMENTS.EFFECTIVE_DATE, value: effectiveDate },
        { dataElement: this.DATA_ELEMENTS.NOTES, value: notes || `Staff assignment: ${previousStaffId ? 'Changed' : 'Initial'}` }
      ]
    };

    if (previousStaffId) {
      eventData.dataValues.push({
        dataElement: this.DATA_ELEMENTS.PREVIOUS_STAFF, 
        value: previousStaffId
      });
    }

    console.log('📝 [DEBUG] Final event data:', eventData);
    
    const result = await this.engine.mutate({
      resource: 'events',
      type: 'create',
      data: eventData
    });

    console.log('✅ [DEBUG] History event created successfully in orgUnit:', orgUnit);
    return { success: true, result: result };
    
  } catch (error) {
    // 🔴 CRITICAL: Handle 409 conflicts gracefully
    if (error.message.includes('409') || error.message.includes('Conflict')) {
      console.warn('⚠️ History event already exists, skipping duplicate');
      return { success: true, duplicate: true };
    }
    
    // 🔴 Handle other 400 errors (like invalid org unit)
    if (error.message.includes('400') || error.message.includes('Bad Request')) {
      console.warn('⚠️ Bad request for history tracking, likely invalid org unit');
      return { success: false, error: 'Invalid org unit for history tracking' };
    }
    
    console.error('❌ Failed to track staff assignment:', error);
    return { success: false, error: error.message };
  }
}

async getPositionHistory(positionTei, user = null) {
    try {
      console.log('🔍 [DEBUG] Getting history for position TEI:', positionTei);
      
      let actualPositionId = positionTei;

      // Get position details first
      try {
        const positionResponse = await this.engine.query({
          tei: {
            resource: 'trackedEntityInstances',
          id: positionTei,
          params: {
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
          }
        }
      });

      if (positionResponse.tei) {
        const attributes = positionResponse.tei.attributes || [];
        const positionIdAttr = attributes.find(attr => 
          attr.attribute === 'SlgO60cMK3F'
        );
        if (positionIdAttr) {
          actualPositionId = positionIdAttr.value;
        }
        
        console.log('✅ [DEBUG] Position belongs to orgUnit:', positionResponse.tei.orgUnit);
        console.log('✅ [DEBUG] Using Position ID for query:', actualPositionId);
      }
    } catch (teiError) {
      console.warn('⚠️ Could not fetch TEI for history:', teiError.message);
    }

    console.log('🎯 [DEBUG] Querying history for Position ID:', actualPositionId);

    const queryParams = {
      program: this.POSITION_HISTORY_PROGRAM,
      programStage: this.POSITION_HISTORY_STAGE,
      filter: [`${this.DATA_ELEMENTS.POSITION_ID}:eq:${actualPositionId}`],
      fields: 'event,orgUnit,eventDate,created,dataValues[dataElement,value]',
      order: 'eventDate:desc,created:desc',
      pageSize: 1000
    };

    if (user && user.organisationUnits && user.organisationUnits.length > 0) {
      const userOrgUnits = user.organisationUnits.map(ou => ou.id).join(';');
      queryParams.ou = userOrgUnits;
      queryParams.ouMode = 'ACCESSIBLE';
      console.log('🔍 [DEBUG] Querying with user org units:', userOrgUnits);
    }

    console.log('📤 [DEBUG] Final query params:', queryParams);

    const response = await this.engine.query({
      history: {
        resource: 'events',
        params: queryParams
      }
    });

    const events = response.history.events || [];
    console.log(`📊 [DEBUG] Found ${events.length} history events for position ${actualPositionId}`);
    
    // Log the org units of found events
    if (events.length > 0) {
      console.log('🏢 [DEBUG] Events found in org units:', events.map(e => e.orgUnit));
    }

    // Transform events
    const history = events.map(event => ({
      id: event.event,
      date: event.eventDate,
      type: this.getDataValue(event, this.DATA_ELEMENTS.ACTION_TYPE),
      staffId: this.getDataValue(event, this.DATA_ELEMENTS.STAFF_MEMBER),
      startDate: this.getDataValue(event, this.DATA_ELEMENTS.START_DATE),
      endDate: this.getDataValue(event, this.DATA_ELEMENTS.END_DATE),
      previousStaffId: this.getDataValue(event, this.DATA_ELEMENTS.PREVIOUS_STAFF),
      notes: this.getDataValue(event, this.DATA_ELEMENTS.NOTES),
      effectiveDate: this.getDataValue(event, this.DATA_ELEMENTS.EFFECTIVE_DATE),
      positionId: this.getDataValue(event, this.DATA_ELEMENTS.POSITION_ID)
    }));

    console.log('📋 [DEBUG] Processed history:', history);
    return history;
    
  } catch (error) {
    console.error('❌ Failed to get position history:', error);
    return [];
  }
}
}

export class ProgramAssignmentService {
  constructor(engine) {
    this.engine = engine;
    this.POSITION_HISTORY_PROGRAM = 'bzvlzdKhtFb';
  }

  /**
   * Automatically assign program to user's org unit and its descendants
   */
  async ensureProgramAssigned(userOrgUnitId) {
    try {
      console.log('🔧 Checking program assignment for org unit:', userOrgUnitId);
      
      // 1. Check if program is already assigned to this org unit
      const isAssigned = await this.isProgramAssigned(userOrgUnitId);
      
      if (isAssigned) {
        console.log('✅ Program already assigned to org unit');
        return true;
      }

      // 2. Get org unit hierarchy (level 5 to 8)
      const orgUnitsToAssign = await this.getOrgUnitHierarchy(userOrgUnitId);
      
      // 3. Assign program to all org units in hierarchy
      await this.assignProgramToOrgUnits(orgUnitsToAssign);
      
      console.log(`✅ Program assigned to ${orgUnitsToAssign.length} org units`);
      return true;
      
    } catch (error) {
      console.error('❌ Failed to assign program:', error);
      return false;
    }
  }

  async isProgramAssigned(orgUnitId) {
    try {
      const response = await this.engine.query({
        program: {
          resource: 'programs',
          id: this.POSITION_HISTORY_PROGRAM,
          params: {
            fields: 'organisationUnits[id]'
          }
        }
      });

      const assignedOrgUnits = response.program.organisationUnits || [];
      const isAssigned = assignedOrgUnits.some(ou => ou.id === orgUnitId);
      console.log(`📊 Program assignment check for ${orgUnitId}: ${isAssigned}`);
      return isAssigned;
      
    } catch (error) {
      console.error('Error checking program assignment:', error);
      return false;
    }
  }

  async getOrgUnitHierarchy(rootOrgUnitId) {
    try {
      console.log('🔍 Getting org unit hierarchy for:', rootOrgUnitId);
      
      // Use the correct API endpoint for org unit with children
      const response = await this.engine.query({
        orgUnit: {
          resource: 'organisationUnits',
          id: rootOrgUnitId,
          params: {
            fields: 'id,name,level,children[id,name,level,children[id,name,level,children[id,name,level]]]',
            paging: false
          }
        }
      });

      console.log('📊 Org unit response:', response);

      const orgUnits = new Set();
      
      // Function to recursively collect org units from level 5 to 8
      const collectOrgUnits = (orgUnit) => {
        console.log(`🏢 Processing: ${orgUnit.name} (Level ${orgUnit.level}) - ID: ${orgUnit.id}`);
        
        // Only include levels 5 to 8
        if (orgUnit.level >= 5 && orgUnit.level <= 8) {
          orgUnits.add(orgUnit.id);
          console.log(`✅ INCLUDED: ${orgUnit.name} (Level ${orgUnit.level})`);
        } else {
          console.log(`❌ EXCLUDED: ${orgUnit.name} (Level ${orgUnit.level}) - outside range 5-8`);
        }
        
        // Recursively process children if they exist
        if (orgUnit.children && orgUnit.children.length > 0) {
          console.log(`👥 ${orgUnit.name} has ${orgUnit.children.length} children`);
          orgUnit.children.forEach(child => {
            collectOrgUnits(child);
          });
        }
      };

      // Start collection from the root org unit
      collectOrgUnits(response.orgUnit);
      
      console.log(`📋 Found ${orgUnits.size} org units in hierarchy (levels 5-8):`, Array.from(orgUnits));
      
      return Array.from(orgUnits);
      
    } catch (error) {
      console.error('❌ Error getting org unit hierarchy:', error);
      console.error('Error details:', error.message);
      
      // Fallback: just assign to the root org unit
      console.log('🔄 Falling back to root org unit only');
      return [rootOrgUnitId];
    }
  }

  async assignProgramToOrgUnits(orgUnitIds) {
    if (!orgUnitIds || orgUnitIds.length === 0) {
      console.log('No org units to assign program to');
      return;
    }

    try {
      console.log(`📤 Assigning program to ${orgUnitIds.length} org units:`, orgUnitIds);
      
      const additions = orgUnitIds.map(id => ({ id }));
      
      const result = await this.engine.mutate({
        resource: `programs/${this.POSITION_HISTORY_PROGRAM}/organisationUnits`,
        type: 'update',
        data: {
          additions: additions
        }
      });

      console.log(`✅ Successfully assigned program to ${orgUnitIds.length} org units:`, result);
      
    } catch (error) {
      console.error('❌ Error assigning program to org units:', error);
      
      // More detailed error logging
      if (error.details && error.details.response) {
        try {
          const errorText = await error.details.response.text();
          console.error('❌ API Error response:', errorText);
        } catch (e) {
          console.error('❌ Could not parse error response:', e);
        }
      }
      throw error;
    }
  }
}